import React, { useState } from "react";
import SocialAuth from "../pages/lmcUserVIew/SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { useTranslation } from "react-i18next";
import {
  HiCheck,
  HiChevronDown,
  HiExclamationCircle,
  HiMail,
} from "react-icons/hi";
import { useUserSocialLogin } from "../../hooks/users";
import { useCardPreview } from "../../contexts/CardPreviewContext";
import { useLmcUserView } from "../../contexts/AuthContext";
import { buildBusinessCardAttribution } from "../../utils/businessCardAttribution";

interface UserRegistrationSectionProps {
  processId: string;
  entityData: {
    entity_identification: string;
    entity_name?: string;
    entity_logo?: string;
  };
  redirectionLinks?: Array<{
    id: string;
    label: string;
    link: string;
  }>;
  onSocialLogin?: (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => void;
  onEmailLogin?: () => void;
  onLoginStart?: () => void;
  onLoginReject?: (err: any) => void;
  onRegistrationSuccess?: () => void;
  className?: string;
  theme?: "classic" | "modern" | "minimal";
  size?: "sm" | "md";
}

const UserRegistrationSection: React.FC<UserRegistrationSectionProps> = ({
  entityData,
  redirectionLinks = [],
  onSocialLogin,
  onEmailLogin,
  onLoginStart = () => {},
  onLoginReject = () => {},
  onRegistrationSuccess,
  className = "",
  theme = "modern",
  size = "sm",
}) => {
  const { t } = useTranslation("clientView");
  const isPreview = useCardPreview();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { mutate: socialLogin } = useUserSocialLogin();

  let cardHolderIdentification = "";
  try {
    const lmcContext = useLmcUserView();
    cardHolderIdentification =
      lmcContext.state.entity.process_data?.user_data?.user_identification ||
      "";
  } catch (_) {}

  const handleSocialResolve = (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => {
    if (onSocialLogin) {
      onSocialLogin(provider, data);
      return;
    }

    const cpPayload = {
      access_token:
        provider === "google"
          ? data?.access_token
          : data?.authorization?.id_token,
      provider,
    };

    const user_utm_data = buildBusinessCardAttribution();

    socialLogin(
      {
        entity_identification: entityData?.entity_identification,
        data: {
          type: "login",
          providerPayload: cpPayload,
          owner_identification: cardHolderIdentification,
          ...(user_utm_data ? { user_utm_data } : {}),
        },
      },
      {
        onSuccess: () => {
          setShowSuccessMessage(true);
          onRegistrationSuccess?.();
        },
        onError: (error: any) => {
          if (error.response?.data?.code === "RSUSKO026" && onEmailLogin) {
            onEmailLogin();
            return;
          }

          const errorMsg = error.response?.data.message || t("key80");
          setShowErrorMessage(true);
          setErrorMessage(errorMsg);

          setTimeout(() => {
            setShowSuccessMessage(false);
            setErrorMessage("");
          }, 4000);
        },
      }
    );
  };

  const getSocialAuthIcons = () => {
    const icons: any = {};

    icons.Apple = (
      <div className="group bg-black hover:bg-gray-800 text-white rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-sm">
        <img src="/images/apple.svg" alt="" className="w-4 h-4" />
        <span className="text-sm font-medium">Apple</span>
      </div>
    );

    icons.Google = (
      <div className="group bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-sm">
        <img src="/images/google.svg" alt="" className="w-4 h-4" />
        <span className="text-sm font-medium">Google</span>
      </div>
    );

    return icons;
  };

  const handleEmailClick = () => {
    onEmailLogin?.();
  };

  if (isPreview) {
    const socialIcons = getSocialAuthIcons();
    return (
      <div
        className={`bg-gradient-to-br from-white to-gray-50/30 border border-gray-200/60 backdrop-blur-sm rounded-xl p-4 shadow-sm ${className}`}
        aria-hidden="true"
      >
        <div className="text-center mb-4">
          <h3 className="text-gray-800 font-semibold text-base leading-tight">
            {t("businessCard.register.heading")}
          </h3>
          <p className="text-gray-500 text-xs mt-0.5">
            {t("businessCard.register.subheading")}
          </p>
        </div>
        <div className="flex gap-2 mb-2">
          <div className="flex-1 pointer-events-none">{socialIcons.Apple}</div>
          <div className="flex-1 pointer-events-none">{socialIcons.Google}</div>
        </div>
        <button
          type="button"
          disabled
          tabIndex={-1}
          aria-hidden="true"
          className="w-full bg-gray-100 text-gray-700 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium border-0"
        >
          <HiMail className="w-4 h-4 text-gray-500" />
          {t("businessCard.register.emailCta")}
        </button>
        {redirectionLinks.length > 0 && (
          <button
            type="button"
            disabled
            tabIndex={-1}
            aria-hidden="true"
            className="w-full mt-3 text-gray-500 text-xs flex items-center justify-center gap-1"
          >
            <span>{t("key84")}</span>
            <HiChevronDown className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`bg-gradient-to-br from-white to-gray-50/30 border border-gray-200/60 backdrop-blur-sm rounded-xl p-4 shadow-sm ${className}`}
    >
      {showSuccessMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <HiCheck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-green-900 text-sm font-semibold">
              {t("businessCard.register.successTitle")}
            </p>
            <p className="text-green-800 text-xs mt-0.5">
              {t("businessCard.register.successBody")}
            </p>
          </div>
        </div>
      )}

      {showErrorMessage && (
        <div className="p-3 sm:p-4 border-b border-gray-100">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center space-x-3">
            <HiExclamationCircle className="w-5 h-5 text-amber-600" />
            <p className="text-amber-800 text-sm font-medium">{errorMessage}</p>
          </div>
        </div>
      )}

      {!showSuccessMessage && (
        <>
          <div className="text-center mb-4">
            <h3 className="text-gray-800 font-semibold text-base leading-tight">
              {t("businessCard.register.heading")}
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">
              {t("businessCard.register.subheading")}
            </p>
          </div>

          <div className="flex gap-2 mb-2">
            <SocialAuth
              className="flex-1"
              providers={["Google", "Apple"]}
              onLoginStartSocialAuthStart={onLoginStart}
              onResolveSocialAuth={handleSocialResolve}
              onRejectSocialAuth={onLoginReject}
              redirectUrl={{
                Apple: `${process.env.REACT_APP_APPLE_CALLBACK}`,
              }}
              providersIcon={getSocialAuthIcons()}
            />
          </div>

          <button
            onClick={handleEmailClick}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 transition-all duration-200 text-sm font-medium border-0 group"
          >
            <HiMail className="w-4 h-4 text-gray-500 group-hover:text-gray-600" />
            {t("businessCard.register.emailCta")}
          </button>

          {redirectionLinks.length > 0 && (
            <>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full mt-3 text-gray-500 hover:text-gray-700 text-xs flex items-center justify-center gap-1 transition-colors duration-200"
              >
                <span>{t("key84")}</span>
                <HiChevronDown
                  className={`w-3 h-3 transition-transform duration-200 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isExpanded && (
                <div className="mt-3 space-y-2 animate-in slide-in-from-top-2 duration-200">
                  {redirectionLinks.map((item) => (
                    <a
                      key={item.id}
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                      className="block w-full bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg px-4 py-2 text-sm font-medium text-center transition-all duration-200 hover:border-gray-300"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default UserRegistrationSection;
